
import { useState, useCallback } from "react";
import { useAuth } from '@/contexts/AuthContext';
import { Message } from './types';
import { useA2AProtocol } from '@/hooks/useA2AProtocol';
import { sendMessageToOrchestrator } from '@/services/orchestratorService';

interface AIAssistantMessage {
  id: string;
  content: string;
  type: 'user' | 'assistant';
  timestamp: Date;
}

interface SuggestedAction {
  id: string;
  label: string;
  action: string;
}

export interface UseAIAssistantMessagesProps {
  config?: any;
}

export const useAIAssistantMessages = (event?: any) => {
  const { session } = useAuth();
  const [messages, setMessages] = useState<AIAssistantMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  
  // Use A2A protocol for agent communication
  const a2aProtocol = useA2AProtocol();

  const addMessage = useCallback((message: AIAssistantMessage) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const generateResponse = useCallback(async (newMessage: string) => {
    if (!session?.user) {
      console.warn('No authenticated user for AI assistant');
      return;
    }
    
    setIsTyping(true);
    
    // Add user message
    const userMessage: AIAssistantMessage = {
      id: Date.now().toString(),
      content: newMessage,
      type: 'user',
      timestamp: new Date()
    };
    
    addMessage(userMessage);
    
    try {
      console.log('Sending message to real AI via orchestrator...');
      
      // Send directly to orchestrator for real AI processing
      const aiResponse = await sendMessageToOrchestrator({
        content: newMessage,
        contextData: event ? { event } : undefined,
        targetAgent: 'executive-agent'
      });
      
      const aiMessage: AIAssistantMessage = {
        id: aiResponse.id,
        content: aiResponse.content,
        type: 'assistant',
        timestamp: aiResponse.timestamp
      };
      
      addMessage(aiMessage);
      
    } catch (error) {
      console.error('Error generating AI response:', error);
      
      // Add fallback error message
      const errorMessage: AIAssistantMessage = {
        id: (Date.now() + 1).toString(),
        content: "I apologize, but I'm experiencing technical difficulties. Please check your API key configuration in Settings > Integrations, or try again later.",
        type: 'assistant',
        timestamp: new Date()
      };
      
      addMessage(errorMessage);
    } finally {
      setIsTyping(false);
    }
  }, [session?.user, addMessage, event]);

  const handleSendMessage = useCallback(async (message: string) => {
    await generateResponse(message);
  }, [generateResponse]);

  const executeSuggestedAction = useCallback(async (action: SuggestedAction) => {
    console.log('Executing action:', action);
    await generateResponse(`Execute action: ${action.label}`);
  }, [generateResponse]);

  const onActionClick = useCallback((action: string) => {
    generateResponse(action);
  }, [generateResponse]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Convert AIAssistantMessage to Message format
  const convertedMessages: Message[] = messages.map(msg => ({
    id: msg.id,
    content: msg.content,
    sender: msg.type === 'user' ? 'user' : 'ai',
    timestamp: msg.timestamp
  }));

  return {
    activeTab,
    setActiveTab,
    messages: convertedMessages,
    isProcessing: isTyping,
    handleSendMessage,
    onActionClick,
    executeSuggestedAction,
    addMessage,
    clearMessages,
  };
};
